import { IntlShape } from 'react-intl';
import { toISOString } from '../../utils/localeHelpers';

/**
 * Períodos de los filtros «Período» (ver `PeriodField`): los atajos (Hoy, Últimos 7 días…), el rango que resuelve cada
 * uno y cómo se nombra en texto. Lo comparten las pantallas con ese filtro (estadísticas de atención, atenciones de
 * urgencia).
 */

/** Un atajo de período; los nombres están en `es.json` (`PeriodFieldPreset…`). */
export type PeriodPreset = 'today' | 'week' | 'month30' | 'thisMonth' | 'lastMonth' | 'last3Months' | 'thisYear';

/** Lo que se elige en «Período»: un atajo o «Personalizado» (las dos fechas a mano). */
export type PeriodChoice = PeriodPreset | 'custom';

/** Los atajos de período en el orden en que se ofrecen. */
export const PERIOD_PRESETS: PeriodPreset[] = ['today', 'week', 'month30', 'thisMonth', 'lastMonth', 'last3Months', 'thisYear'];

/** Cómo se llama cada atajo: con «Ver solo este día» se ofrece «Volver a …» con el nombre del período que había. */
export const PERIOD_PRESET_NAME_ID: Record<PeriodPreset, string> = {
    today: 'PeriodFieldPresetToday',
    week: 'PeriodFieldPresetWeek',
    month30: 'PeriodFieldPresetMonth30',
    thisMonth: 'PeriodFieldPresetThisMonth',
    lastMonth: 'PeriodFieldPresetLastMonth',
    last3Months: 'PeriodFieldPresetLast3Months',
    thisYear: 'PeriodFieldPresetThisYear',
};

export function addDays(date: Date, days: number): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

export function isoDate(date: Date): string {
    return toISOString(date)!.substring(0, 10);
}

/** El mismo día de hace `months` meses (el último del mes si ese día no existe: 31/12 menos 3 meses es 30/9). */
function monthsAgo(day: Date, months: number): Date {
    const lastDay = new Date(day.getFullYear(), day.getMonth() - months + 1, 0).getDate();
    return new Date(day.getFullYear(), day.getMonth() - months, Math.min(day.getDate(), lastDay));
}

export function presetRange(preset: PeriodPreset, today: Date): { from: Date; to: Date } {
    const day = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    switch (preset) {
        case 'today': return { from: day, to: day };
        case 'week': return { from: addDays(day, -6), to: day };
        case 'month30': return { from: addDays(day, -29), to: day };
        case 'thisMonth': return { from: new Date(day.getFullYear(), day.getMonth(), 1), to: day };
        case 'lastMonth': return {
            from: new Date(day.getFullYear(), day.getMonth() - 1, 1),
            to: new Date(day.getFullYear(), day.getMonth(), 0),
        };
        // Tres meses corridos hasta hoy inclusive: el 25/10 va del 26/7 al 25/10
        case 'last3Months': return { from: addDays(monthsAgo(day, 3), 1), to: day };
        case 'thisYear': return { from: new Date(day.getFullYear(), 0, 1), to: day };
    }
}

/** El atajo que coincide con las fechas, si hay alguno. */
export function activePreset(from: Date | null, to: Date | null, today: Date): PeriodPreset | null {
    if (!from || !to) return null;
    const match = PERIOD_PRESETS.find(id => {
        const range = presetRange(id, today);
        return isoDate(range.from) === isoDate(from) && isoDate(range.to) === isoDate(to);
    });
    return match ?? null;
}

/** "25/9" o, con año, "25/9/2026": la fecha corta con que se nombra un período en las frases y las variaciones. */
export function formatShortDate(date: Date, withYear: boolean): string {
    const day = `${date.getDate()}/${date.getMonth() + 1}`;
    return withYear ? `${day}/${date.getFullYear()}` : day;
}

/** El año solo se escribe si el período no es del año de `today` (o cruza de un año a otro). */
export function periodNeedsYear(from: Date, to: Date, today: Date): boolean {
    return from.getFullYear() !== to.getFullYear() || to.getFullYear() !== today.getFullYear();
}

/** Lo que muestra «Período»: el atajo que coincide con las fechas, salvo que se haya elegido «Personalizado». */
export function periodChoiceOf(from: Date | null, to: Date | null, custom: boolean, today: Date): PeriodChoice {
    return (custom ? null : activePreset(from, to, today)) ?? 'custom';
}

/** "25/9 al 24/10" (o solo el día, en «Hoy»): el rango de un atajo en texto chico bajo «Período». Vacío si no hay rango. */
export function rangeTextOf(intl: IntlShape, from: Date | null, to: Date | null, today: Date): string {
    if (!from || !to) return '';
    const withYear = periodNeedsYear(from, to, today);
    const fromText = formatShortDate(from, withYear);
    const toText = formatShortDate(to, withYear);
    return fromText === toText ? fromText : intl.formatMessage({ id: 'PeriodFieldRange' }, { from: fromText, to: toText });
}
